import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../../../hook/useRegistrationStepFocus";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  mapRdInfoToProfile,
} from "../../Appointment/registrationNoPreferenceTimeslots";
import {
  getRegistrationData,
  updateRegistrationData,
  isCardOnFileComplete,
} from "../../registrationSetupStorage";
import { registrationGoBack } from "../../registrationNavigation";
import RegistrationRdAvatar from "../RegistrationRdAvatar";

const PROFILE_STEP = REGISTRATION_SETUP_STEP_META.DIETITIAN_PROFILE;

const RegistrationNoPreferenceDietitianProfile = ({ history, match }) => {
  const { t } = useTranslation();
  const stepTitleRef = useRegistrationStepFocus();
  const rdId = match.params.rdId;
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const data = getRegistrationData();
    if (!isCardOnFileComplete(data)) {
      history.replace("/register/account/card");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }
    if (!data.noPreferenceSelectedSlot) {
      history.replace("/register/account/no-preference/appointments");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    const rdList = data.noPreferenceRdList ?? data.noPreferenceSlotData?.rd_info ?? [];
    const rdInfo =
      rdList.find((item) => String(item?.id) === String(rdId)) ??
      data.noPreferenceSelectedRd?.raw;

    if (!rdInfo) {
      history.replace("/register/account/no-preference/appointments");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    const mapped = mapRdInfoToProfile(rdInfo);
    setProfile(mapped);
    updateRegistrationData({
      noPreferenceSelectedRd: {
        rdId: String(rdId),
        raw: rdInfo,
        view: mapped,
      },
      selectedRdProfile: {
        rdId: String(rdId),
        raw: rdInfo,
        view: mapped,
      },
      selectedDietitian: {
        id: rdId,
        name: mapped?.name ?? t("NUTRITION_COMMON_LBL_RD_FALLBACK"),
        noPreference: true,
        raw: rdInfo,
      },
    });

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history, rdId]);

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={PROFILE_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationGoBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card content-card-profile">
            <div className="content-card-scroll">
              {!profile ? (
                <p className="section-sub">{t("NUTRITION_RDPROFILE_LBL_LOADING")}</p>
              ) : (
                <>
                  <div className="rd-profile-hero">
                    <RegistrationRdAvatar
                      className="rd-profile-avatar"
                      profileImage={profile.profileImage}
                      initials={profile.initials}
                      avatarStyle={profile.avatarStyle}
                      alt={profile.name}
                    />
                    <div>
                      <div
                        className="rd-profile-name"
                        ref={stepTitleRef}
                        {...REGISTRATION_STEP_TITLE_PROPS}
                      >
                        {profile.name}
                      </div>
                      {profile.creds ? (
                        <div className="rd-profile-creds">{profile.creds}</div>
                      ) : null}
                      <div className="rd-profile-badge">
                        <span aria-hidden>✓</span>{" "}
                        {t("NUTRITION_RDPROFILE_LBL_ACCEPTING_PATIENTS")}
                      </div>
                    </div>
                  </div>

                  {profile.about ? (
                    <div className="rd-detail-section">
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_ABOUT")}
                      </div>
                      <div className="rd-detail-text">{profile.about}</div>
                    </div>
                  ) : null}

                  {profile.specialties.length > 0 ? (
                    <div className="rd-detail-section">
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_SPECIALTIES")}
                      </div>
                      <div className="rd-detail-categories">
                        {profile.specialties.map((item) => (
                          <span key={item} className="rd-detail-category">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {profile.licensedIn.length > 0 ? (
                    <div
                      className={`rd-detail-section${
                        !profile.approach ? " rd-detail-section-last" : ""
                      }`}
                    >
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_LICENSED_IN")}
                      </div>
                      <div className="rd-detail-chips">
                        {profile.licensedIn.map((stateCode) => (
                          <span
                            key={stateCode}
                            className="rd-detail-chip rd-detail-chip-state"
                          >
                            {stateCode}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {profile.approach ? (
                    <div className="rd-detail-section rd-detail-section-last">
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_APPROACH")}
                      </div>
                      <div className="rd-detail-text">{profile.approach}</div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </RegistrationLayout>
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationNoPreferenceDietitianProfile);
